import React, { useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ExternalLink } from "lucide-react";
import PageShell from "@/components/PageShell";
import TraditionTag from "@/components/TraditionTag";
import ScripturalAnchor from "@/components/ScripturalAnchor";
import { getSongCard, formatDuration } from "@/lib/catalog";
import { addUtm } from "@/lib/utm";
import { trackEvent } from "@/lib/tracking";
import { ytEmbedUrl } from "@/lib/youtube";

function spotifyTrackId(url) {
  if (!url) return null;
  const m = url.match(/spotify\.com\/(track)\/([A-Za-z0-9]+)/);
  return m ? m[2] : null;
}

function spotifyAlbumId(url) {
  if (!url) return null;
  const m = url.match(/spotify\.com\/album\/([A-Za-z0-9]+)/);
  return m ? m[1] : null;
}

function PlatformLinks({ song, campaign }) {
  const links = [
    song.spotify_url && {
      id: "spotify",
      label: "Spotify",
      url: song.spotify_url,
    },
    song.apple_music_url && {
      id: "apple",
      label: "Apple Music",
      url: song.apple_music_url,
    },
    song.youtube_music_url && {
      id: "yt-music",
      label: "YouTube Music",
      url: song.youtube_music_url,
    },
    song.youtube_video_id && {
      id: "youtube",
      label: "YouTube",
      url: `https://www.youtube.com/watch?v=${song.youtube_video_id}`,
    },
  ].filter(Boolean);

  if (links.length === 0) {
    return (
      <p className="font-body text-sm text-ink-mute italic">
        Streaming links coming soon.
      </p>
    );
  }
  return (
    <div className="flex flex-wrap gap-2.5">
      {links.map((l, i) => (
        // BUG FIXED 2026-08-20: these links were UTM-stamped but fired no
        // event at all, so `music_platform_click` had no emitter anywhere in
        // the frontend. The strategy reads its zero count as evidence that the
        // music conversion "is not demonstrably alive" and bars spend on it —
        // a conclusion drawn from an event the site was never able to send.
        <a
          key={l.id}
          href={addUtm(l.url, { campaign, content: l.id })}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() =>
            trackEvent("music_platform_click", {
              title_id: song.slug,
              slug: song.slug,
              store: l.id,
              song_title: song.title,
              outbound_url: addUtm(l.url, { campaign, content: l.id }),
            })
          }
          className={`btn-pill ${i === 0 ? "btn-pill-solid" : ""}`}
          data-track="music-out"
          data-platform={l.id}
        >
          {l.label}
          <ExternalLink size={13} />
        </a>
      ))}
    </div>
  );
}

export default function MusicDetail() {
  const { slug } = useParams();
  // The same detail page serves /listen/:slug and the older /music/:slug. Send
  // the reader back where they came from rather than to whichever name this
  // file happened to be written against.
  const { pathname } = useLocation();
  const base = pathname.startsWith("/listen") ? "/listen" : "/music";
  const card = getSongCard(slug);
  const [activeIdx, setActiveIdx] = useState(0);

  if (!card) {
    return (
      <PageShell title="Not found">
        <div className="max-w-2xl mx-auto px-6 py-32 text-center">
          <h1 className="font-display text-3xl text-ink">Track not found</h1>
          <p className="font-serif text-ink-soft mt-3">
            <Link to={base} className="underline decoration-dharma-gold">
              Back to all music
            </Link>
          </p>
        </div>
      </PageShell>
    );
  }

  const active = card.members[activeIdx] || card.representative;
  const spId = spotifyTrackId(active.spotify_url);
  const ytId = active.youtube_video_id;
  // An album plays as an album: one player for all its tracks.
  const albumSpId = card.is_album ? spotifyAlbumId(card.album_links?.spotify_url) : null;

  return (
    <PageShell title={card.title}>
      <div className="max-w-8xl mx-auto px-5 md:px-10 lg:px-14 pt-6 md:pt-10">
        <Link
          to={base}
          className="inline-flex items-center gap-2 font-body text-xs tracking-widest2 uppercase text-ink-mute hover:text-dharma-gold-deep transition-colors"
        >
          <ArrowLeft size={14} />
          All music
        </Link>
      </div>

      <section className="max-w-8xl mx-auto px-5 md:px-10 lg:px-14 pt-10 md:pt-14 pb-16 md:pb-20">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <TraditionTag tradition={card.tradition} />
          <h1 className="font-display text-4xl md:text-6xl text-ink leading-[1.05] tracking-tightish mt-5 max-w-4xl">
            {card.title}
          </h1>
          {card.anchor && (
            <div className="mt-7 max-w-3xl">
              <ScripturalAnchor anchor={card.anchor} />
            </div>
          )}
          {card.intro && (
            <p className="font-serif text-ink-soft text-lg md:text-xl leading-relaxed mt-6 max-w-3xl">
              {card.intro}
            </p>
          )}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-12 md:mt-16 items-start">
          <div className="lg:col-span-7">
            {/* Embedded player — YouTube preferred (renders globally), then Spotify as fallback */}
            <div className="card-shrine rounded-sm overflow-hidden bg-shrine-shadow">
              {albumSpId ? (
                <iframe
                  src={`https://open.spotify.com/embed/album/${albumSpId}?utm_source=generator`}
                  width="100%"
                  height="452"
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                  title={card.title}
                  style={{ border: 0 }}
                />
              ) : ytId ? (
                <div className="aspect-video">
                  <iframe
                    src={ytEmbedUrl(ytId)}
                    title={active.title}
                    allow="autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
              ) : spId ? (
                <iframe
                  src={`https://open.spotify.com/embed/track/${spId}?utm_source=generator`}
                  width="100%"
                  height="352"
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                  title={active.title}
                  style={{ border: 0 }}
                />
              ) : card.cover_url ? (
                <img
                  src={card.cover_url}
                  alt={card.title}
                  className="w-full aspect-square object-cover"
                />
              ) : (
                <div className="aspect-video flex items-center justify-center text-cream/60 font-serif italic">
                  Audio coming soon
                </div>
              )}
            </div>

            <div className="mt-7">
              <div className="eyebrow mb-3">Listen on</div>
              <PlatformLinks song={card.album_links || active} campaign={`music-${card.slug}`} />
            </div>
          </div>

          <div className="lg:col-span-5 lg:pl-4">
            {card.is_group && card.members.length > 1 && (
              <div className="mb-8">
                <div className="eyebrow mb-3">{card.members_label || "Versions"}</div>
                <div className="space-y-2">
                  {card.members.map((m, i) => {
                    const isActive = i === activeIdx;
                    return (
                      <button
                        key={m.id}
                        onClick={() => setActiveIdx(i)}
                        className={`w-full text-left px-4 py-3 rounded-sm border transition-all duration-500 ease-breath ${
                          isActive
                            ? "bg-ink text-cream border-ink"
                            : "bg-parchment/50 text-ink border-dharma-gold/20 hover:border-dharma-gold hover:bg-parchment"
                        }`}
                      >
                        <div className="font-display text-sm">{card.member_labels?.[i] || m.title}</div>
                        {m.duration_seconds > 0 && (
                          <div
                            className={`font-body text-[0.65rem] tracking-widest2 uppercase mt-1 ${
                              isActive ? "text-cream/60" : "text-ink-mute"
                            }`}
                          >
                            {formatDuration(m.duration_seconds)}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {active.description && (
              <div>
                <div className="eyebrow mb-3">About</div>
                <p className="font-serif text-ink-soft text-lg leading-relaxed">
                  {active.description}
                </p>
              </div>
            )}

            {active.themes?.length > 0 && (
              <div className={active.description ? "mt-7" : ""}>
                <div className="eyebrow mb-3">Themes</div>
                <div className="flex flex-wrap gap-2">
                  {active.themes.map((t) => (
                    <span key={t} className="tradition-tag">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {card.is_album && card.album_links && (
              <div className="mt-7">
                <div className="eyebrow mb-3">This track on</div>
                <PlatformLinks song={active} campaign={`music-${card.slug}-track`} />
              </div>
            )}
          </div>
        </div>
      </section>
    </PageShell>
  );
}
